// src/components/MessageList/MessageList.tsx
import React, { useEffect, useRef } from "react";
import type { ChatMessage } from "../../types";
import { stripMarkdown } from "../../utils/markdown";
import "./MessageList.scss";

interface MessageListProps {
    messages: ChatMessage[];
    /** 当前正在播的那一句 */
    speechRange?: { start: number; end: number } | null;
}

function SpokenRange({
    text,
    start,
    end,
}: {
    text: string;
    start: number;
    end: number;
}) {
    const a = Math.max(0, Math.min(text.length, start));
    const b = Math.max(a, Math.min(text.length, end));
    return (
        <>
            {text.slice(0, a)}
            <span className="msg-read">{text.slice(a, b)}</span>
            {text.slice(b)}
        </>
    );
}

export const MessageList: React.FC<MessageListProps> = ({
    messages,
    speechRange = null,
}) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const speakAt =
        speechRange == null
            ? -1
            : messages.reduce(
                  (found, message, index) =>
                      message.type === "assistant" &&
                      message.content &&
                      !message.isThinking
                          ? index
                          : found,
                  -1,
              );

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const getMessageKey = (message: ChatMessage, index: number): string => {
        if (message.id) {
            return message.id;
        }
        return `msg-${index}-${message.timestamp}`;
    };

    if (messages.length === 0) {
        return null;
    }

    return (
        <div className="message-list">
            {messages.map((message, index) => {
                const isUser = message.type === "user";
                const key = getMessageKey(message, index);
                const isLoading = message.isThinking || message.isRecognizing;

                return (
                    <div
                        key={key}
                        className={`message-item ${isUser ? "msg-user" : "msg-assistant"}${isLoading ? " msg-thinking" : ""}${index === speakAt ? " is-speaking" : ""}`}
                    >
                        <div className="msg-avatar">
                            {isUser ? (
                                "👤"
                            ) : (
                                <img
                                    src="/avatar.png"
                                    alt="金小卫"
                                />
                            )}
                        </div>
                        <div className="msg-bubble">
                            {isLoading ? (
                                <div className="msg-thinking-content">
                                    <span className="thinking-dots">
                                        <span className="dot" />
                                        <span className="dot" />
                                        <span className="dot" />
                                    </span>
                                </div>
                            ) : (
                                <pre className="msg-text">
                                    {isUser ? (
                                        message.content
                                    ) : index === speakAt && speechRange ? (
                                        <SpokenRange
                                            text={stripMarkdown(message.content)}
                                            start={speechRange.start}
                                            end={speechRange.end}
                                        />
                                    ) : (
                                        stripMarkdown(message.content)
                                    )}
                                </pre>
                            )}
                        </div>
                    </div>
                );
            })}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default MessageList;
